import type { Canvas } from 'fabric';
import type { SetStateAction } from 'react';
import { type MutableRefObject, useCallback } from 'react';
import type { SelectedObjectState } from '../store/atoms/editorSelectionAtom';
import type { ActiveLanguage } from '../store/atoms/editorUiAtom';
import type { VariantKey } from '../store/atoms/slideAtom';
import { isBannerCtaOrChild } from './useObjectTransform';

export interface UseCanvasEventsParams {
  readonly desktopCanvasRef: MutableRefObject<Canvas | null>;
  readonly mobileCanvasRef: MutableRefObject<Canvas | null>;
  readonly activeLanguage: ActiveLanguage;
  readonly activeLanguageRef: MutableRefObject<ActiveLanguage>;
  readonly syncSelectionAtom: (canvas: Canvas) => void;
  readonly setSelection: (value: SetStateAction<SelectedObjectState | null>) => void;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly pushSnapshot: (key: string) => void;
  readonly resetHistory: (key: string) => void;
  readonly setIsDirty: (dirty: boolean) => void;
  readonly setCanvasReadyTick: (update: SetStateAction<number>) => void;
}

/**
 * Liga os listeners de eventos do Fabric.js a cada canvas e expõe os callbacks
 * `onDesktopCanvasReady` / `onMobileCanvasReady` chamados quando o canvas monta.
 *
 * @param params - Refs dos canvas, sub-hooks de sync/histórico e setters de estado
 * @returns `onDesktopCanvasReady`, `onMobileCanvasReady`
 */
export function useCanvasEvents({
  desktopCanvasRef,
  mobileCanvasRef,
  activeLanguage,
  activeLanguageRef,
  syncSelectionAtom,
  setSelection,
  saveCurrentSlide,
  pushSnapshot,
  resetHistory,
  setIsDirty,
  setCanvasReadyTick,
}: UseCanvasEventsParams) {
  const attachCanvasEvents = useCallback(
    (canvas: Canvas, device: 'desktop' | 'mobile') => {
      canvas.on('selection:created', () => syncSelectionAtom(canvas));
      canvas.on('selection:updated', () => syncSelectionAtom(canvas));
      canvas.on('selection:cleared', () => setSelection(null));
      canvas.on('object:added', () => setIsDirty(true));
      canvas.on('object:removed', () => setIsDirty(true));
      canvas.on('object:moving', e => {
        // Impede que o Banner CTA e seus filhos sejam movidos.
        // lockMovementX/Y no grupo não é suficiente no Fabric.js v7 com
        // interactive: true, pois os filhos sempre se tornam o objeto ativo.
        if (!isBannerCtaOrChild(e.target)) return;
        const orig = (e.transform as unknown as { original?: { left: number; top: number } })
          ?.original;
        if (orig) e.target.set({ left: orig.left, top: orig.top });
      });
      canvas.on('object:rotating', e => {
        if (!isBannerCtaOrChild(e.target)) return;

        const transform = e.transform as unknown as {
          original?: { angle: number; left: number; top: number };
        };

        const orig = transform?.original;

        if (orig) {
          e.target.set({
            angle: orig.angle,
            left: orig.left,
            top: orig.top,
          });

          e.target.setCoords();
        }
      });
      canvas.on('object:scaling', e => {
        if (!isBannerCtaOrChild(e.target)) return;

        const transform = e.transform as unknown as {
          original?: {
            scaleX: number;
            scaleY: number;
            left: number;
            top: number;
          };
        };

        const orig = transform?.original;

        if (!orig) return;

        e.target.set({
          scaleX: orig.scaleX,
          scaleY: orig.scaleY,
          left: orig.left,
          top: orig.top,
        });

        e.target.setCoords();
      });
      canvas.on('object:modified', () => {
        const currentKey: VariantKey = `${activeLanguageRef.current}-${device}`;
        saveCurrentSlide(currentKey);
        pushSnapshot(currentKey);
        syncSelectionAtom(canvas);
        setIsDirty(true);
      });
      canvas.on('text:changed', () => {
        const currentKey: VariantKey = `${activeLanguageRef.current}-${device}`;
        saveCurrentSlide(currentKey);
        setIsDirty(true);
      });
    },
    [
      syncSelectionAtom,
      setSelection,
      saveCurrentSlide,
      pushSnapshot,
      activeLanguageRef,
      setIsDirty,
    ],
  );

  const onDesktopCanvasReady = useCallback(
    (canvas: Canvas) => {
      desktopCanvasRef.current = canvas;
      const key: VariantKey = `${activeLanguage}-desktop`;
      attachCanvasEvents(canvas, 'desktop');
      resetHistory(key);
      pushSnapshot(key);
      setCanvasReadyTick(t => t + 1);
    },
    [
      attachCanvasEvents,
      pushSnapshot,
      resetHistory,
      activeLanguage,
      desktopCanvasRef,
      setCanvasReadyTick,
    ],
  );

  const onMobileCanvasReady = useCallback(
    (canvas: Canvas) => {
      mobileCanvasRef.current = canvas;
      const key: VariantKey = `${activeLanguage}-mobile`;
      attachCanvasEvents(canvas, 'mobile');
      resetHistory(key);
      pushSnapshot(key);
      setCanvasReadyTick(t => t + 1);
    },
    [
      attachCanvasEvents,
      pushSnapshot,
      resetHistory,
      activeLanguage,
      mobileCanvasRef,
      setCanvasReadyTick,
    ],
  );

  return { onDesktopCanvasReady, onMobileCanvasReady } as const;
}
